/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { FeatureFlag } from '@ibm-watson/shared/ts/feature-flags';
import { links } from '../components/links';

export const CATALOG_CHANNEL_TYPE = 'catalog';
export const CONVERSATIONAL_SKILL_CHANNEL_TYPE = 'conversational_skill';
export const FACEBOOK_CHANNEL_TYPE = 'facebook';
export const GENESYS_BOT_CONNECTOR_CHANNEL_TYPE = 'genesysbotconnector';
export const SLACK_CHANNEL_TYPE = 'slack';
export const INTERCOM_CHANNEL_TYPE = 'intercom';
export const TEAMS_CHANNEL_TYPE = 'teams';
export const PREVIEW_CHANNEL_TYPE = 'preview_link';
export const WEBCHAT_CHANNEL_TYPE = 'chat';
export const PHONE_CHANNEL_TYPE = 'phone';
export const SMS_CHANNEL_TYPE = 'twilio'; // TO-DO: sync with store + backend to see if this could be renamed 'sms'

export const SMS_CHANNEL_TYPE_TWILIO = 'twilio';
export const SMS_CHANNEL_TYPE_GENESYS = 'genesys';
export const SMS_CHANNEL_TYPE_INTELEPEER = 'intelepeer';
export const SMS_CHANNEL_TYPE_BYOPSMS = 'byopsms';
export const SMS_CHANNEL_TYPES = [
  SMS_CHANNEL_TYPE_TWILIO,
  SMS_CHANNEL_TYPE_GENESYS,
  SMS_CHANNEL_TYPE_INTELEPEER,
  SMS_CHANNEL_TYPE_BYOPSMS
];

export const WHATSAPP_CHANNEL_TYPE = 'twiliowhatsapp';

export const ZENDESK_SERVICEDESK_TYPE = 'zendeskweb';
export const SALESFORCE_SERVICEDESK_TYPE = 'salesforceweb';
export const GENESYS_SERVICEDESK_TYPE = 'genesysweb';

export const AMAZON_CONNECT_BYOSD_TYPE = 'amazon_connect';
export const CONTACT_CENTER_BYOSD_TYPE = 'contact_center';
export const EGAIN_BYOSD_TYPE = 'egain';
export const ENGHOUSE_BYOSD_TYPE = 'enghouse';
export const GENESYS_BYOSD_TYPE = GENESYS_SERVICEDESK_TYPE;
export const GENESYS_AUDIO_CONNECTOR_TYPE = 'genesysAudioConnector';
export const INTELEPEER_BYOSD_TYPE = 'intelepeerweb';
export const NICE_INCONTACT_BYOSD_TYPE = 'nice_cxone';
export const KUSTOMER_BYOSD_TYPE = 'kustomerweb';
export const ORACLE_BYOSD_TYPE = 'oracleweb';
export const TWILIO_BYOSD_TYPE = 'twilioweb';
export const WEB_BYOSD_TYPE = 'byoweb';

// These 4 below are the types used under SMS provider for settings.mobile_messaging_provider.type and not for top level SMS types
export const INTELEPEER_SMS_TYPE = 'Intelepeer';
export const TWILIO_SMS_TYPE = 'Twilio';
export const GENESYS_SMS_TYPE = 'Genesys';
export const CUSTOM_SMS_TYPE = 'BYOPSMS';

export const SEARCH_SKILL_TYPE = 'search';
export const SEGMENT_EXTENSION_TYPE = 'segment';

export const channelTypeToChannelName = {
  [CATALOG_CHANNEL_TYPE]: 'Custom extension',
  [CONVERSATIONAL_SKILL_CHANNEL_TYPE]: 'Conversational Skill',
  [FACEBOOK_CHANNEL_TYPE]: 'Facebook messenger',
  [GENESYS_BOT_CONNECTOR_CHANNEL_TYPE]: 'Genesys Bot Connector',
  [SLACK_CHANNEL_TYPE]: 'Slack',
  [INTERCOM_CHANNEL_TYPE]: 'Intercom',
  [TEAMS_CHANNEL_TYPE]: 'Microsoft teams',
  [PREVIEW_CHANNEL_TYPE]: 'Preview',
  [WEBCHAT_CHANNEL_TYPE]: 'Web chat',
  [PHONE_CHANNEL_TYPE]: 'Phone',
  [WHATSAPP_CHANNEL_TYPE]: 'WhatsApp with Twilio',
  [ZENDESK_SERVICEDESK_TYPE]: 'Zendesk',
  [SALESFORCE_SERVICEDESK_TYPE]: 'Salesforce',
  [SEARCH_SKILL_TYPE]: 'Search',
  [SEGMENT_EXTENSION_TYPE]: 'Segment integration',
  [AMAZON_CONNECT_BYOSD_TYPE]: 'Amazon Connect',
  [WEB_BYOSD_TYPE]: 'Bring your own',
  [EGAIN_BYOSD_TYPE]: 'eGain',
  [ENGHOUSE_BYOSD_TYPE]: 'Enghouse',
  [GENESYS_BYOSD_TYPE]: 'Genesys SIP',
  [GENESYS_AUDIO_CONNECTOR_TYPE]: 'Genesys Audio Connector',
  [INTELEPEER_BYOSD_TYPE]: 'IntelePeer',
  [NICE_INCONTACT_BYOSD_TYPE]: 'NICE inContact',
  [KUSTOMER_BYOSD_TYPE]: 'Kustomer',
  [ORACLE_BYOSD_TYPE]: 'Oracle',
  [TWILIO_BYOSD_TYPE]: 'Twilio',
  [SMS_CHANNEL_TYPE]: 'SMS', // Name for SMS in New IA
  smsClassic: 'SMS with Twilio', // Name for SMS in Classic IA
  byoPhone: 'Bring your own SIP trunk',
  twilioFlex: 'Twilio Flex'
};

export const smsServiceDeskNames = {
  [INTELEPEER_SMS_TYPE]: 'SMS with IntelePeer',
  [TWILIO_SMS_TYPE]: 'SMS with Twilio',
  [GENESYS_SMS_TYPE]: 'SMS with Genesys',
  [CUSTOM_SMS_TYPE]: 'SMS with your proprietary system'
};

export const smsProviderToSMSChannelType = {
  [INTELEPEER_SMS_TYPE]: 'intelepeer',
  [TWILIO_SMS_TYPE]: 'twilio',
  [GENESYS_SMS_TYPE]: 'genesys',
  [CUSTOM_SMS_TYPE]: 'byopsms'
};

export const serviceDeskDefaultChannelName = {
  [WEBCHAT_CHANNEL_TYPE]: 'Bring your own',
  [PHONE_CHANNEL_TYPE]: 'Bring your own SIP trunk'
};

export const getServiceDeskChannelName = (
  serviceDeskType: string,
  parentChannelType: string
): string =>
  channelTypeToChannelName[serviceDeskType] ??
  serviceDeskDefaultChannelName[parentChannelType];

export const channelTypeToChannelDescription = {
  [CATALOG_CHANNEL_TYPE]: '',
  [FACEBOOK_CHANNEL_TYPE]:
    'Make the assistant available to customers through Facebook Messenger on the web or on native mobile clients',
  [GENESYS_BOT_CONNECTOR_CHANNEL_TYPE]:
    'Connect your assistant to other chat platforms using Genesys Bot Connector',
  [SLACK_CHANNEL_TYPE]:
    'Make the assistant available to customers by adding it to a Slack app as a bot user',
  [INTERCOM_CHANNEL_TYPE]:
    'Connect the assistant to an Intercom app so it can help human agents address customer queries',
  [TEAMS_CHANNEL_TYPE]: '',
  [PREVIEW_CHANNEL_TYPE]:
    'Embed the assistant in a chat widget hosted on an IBM-branded web page that your team can use for testing',
  [WEBCHAT_CHANNEL_TYPE]:
    'Embed the web chat into your company website so it can answer questions and transfer customers to human agents',
  [PHONE_CHANNEL_TYPE]:
    'Give your system a voice to help customers over the phone',
  [SMS_CHANNEL_TYPE]: 'Send and receive SMS text messages',
  [WHATSAPP_CHANNEL_TYPE]:
    'Make the assistant available to customers by adding it to WhatsApp',
  [ZENDESK_SERVICEDESK_TYPE]: '',
  [SALESFORCE_SERVICEDESK_TYPE]: '',
  [SEARCH_SKILL_TYPE]: '',
  [SEGMENT_EXTENSION_TYPE]: ''
};

export const channelTypeToClassicTemplateId = {
  [CATALOG_CHANNEL_TYPE]: 'f1512f73-4702-4579-a8a6-41a941979c6b',
  [FACEBOOK_CHANNEL_TYPE]: 'f849dd55-46d3-44eb-b026-01c777ac1521',
  [GENESYS_BOT_CONNECTOR_CHANNEL_TYPE]: '',
  [SLACK_CHANNEL_TYPE]: 'c6b83e7e-cbf9-4c2a-acf9-672cd804a0bb',
  [INTERCOM_CHANNEL_TYPE]: '0be3fbc4-112c-4151-a704-4c2c218e13b3',
  [TEAMS_CHANNEL_TYPE]: '',
  [PREVIEW_CHANNEL_TYPE]: 'c8b9bdb6-5bcd-491e-8f0f-697e4cd7466a',
  [WEBCHAT_CHANNEL_TYPE]: '9de32257-f523-4784-8f8c-9db5046d3cda',
  [PHONE_CHANNEL_TYPE]: 'b0997b75-c4fa-4560-b10b-982992918751',
  [SMS_CHANNEL_TYPE]: 'd2577422-ecbe-43fe-ab79-c2c69c27e594',
  [WHATSAPP_CHANNEL_TYPE]: '5f48264a-841e-4259-a26e-0cf445df380d',
  [ZENDESK_SERVICEDESK_TYPE]: 'c71468db-6cb0-4306-97b2-afc100df0399',
  [SALESFORCE_SERVICEDESK_TYPE]: '4d7cd79f-b1d0-4590-bab2-2ab96612b673'
};

export const channelTypeAltTexts = {
  [FACEBOOK_CHANNEL_TYPE]: 'Facebook integration',
  [GENESYS_BOT_CONNECTOR_CHANNEL_TYPE]: 'Genesys bot connector',
  [SLACK_CHANNEL_TYPE]: 'Slack integration',
  [PREVIEW_CHANNEL_TYPE]: 'Preview integration',
  [WEBCHAT_CHANNEL_TYPE]: 'Web chat integration',
  [PHONE_CHANNEL_TYPE]: 'Voice telephony integration',
  [INTERCOM_CHANNEL_TYPE]: 'Intercom integration',
  [SMS_CHANNEL_TYPE]: 'SMS integration',
  [WHATSAPP_CHANNEL_TYPE]: 'WhatsApp integration',
  [CATALOG_CHANNEL_TYPE]: 'Catalog OpenAPI'
};

export const channelTypeLinks = {
  [ENGHOUSE_BYOSD_TYPE]: links.ENGHOUSE_LINK,
  [WEB_BYOSD_TYPE]: links.BYOSD_LINK,
  [TWILIO_BYOSD_TYPE]: links.TWILIO_BYOSD_LINK,
  [GENESYS_BYOSD_TYPE]: links.GENESYS_BYOSD_LINK,
  [NICE_INCONTACT_BYOSD_TYPE]: links.NICE_INCONTACT_LINK,
  [ORACLE_BYOSD_TYPE]: links.ORACLE_LINK,
  [KUSTOMER_BYOSD_TYPE]: links.KUSTOMER_BYOSD_LINK,
  [EGAIN_BYOSD_TYPE]: links.EGAIN_LINK,
  default: links.BYOSD_LINK
};

export const smsProviderURLs = {
  [INTELEPEER_SMS_TYPE]: 'https://api.intelepeer.com',
  [TWILIO_SMS_TYPE]: 'https://api.twilio.com',
  [GENESYS_SMS_TYPE]: 'https://api.cac1.pure.cloud',
  [CUSTOM_SMS_TYPE]: ''
};

export const channelCardIntegrations = [
  FACEBOOK_CHANNEL_TYPE,
  SLACK_CHANNEL_TYPE,
  GENESYS_BOT_CONNECTOR_CHANNEL_TYPE,
  // INTERCOM_CHANNEL_TYPE,
  TEAMS_CHANNEL_TYPE,
  WEBCHAT_CHANNEL_TYPE,
  PHONE_CHANNEL_TYPE,
  WHATSAPP_CHANNEL_TYPE,
  // Store expects to have these 4 type of SMS integrations (and not simply of type sms)
  SMS_CHANNEL_TYPE_TWILIO,
  SMS_CHANNEL_TYPE_GENESYS,
  SMS_CHANNEL_TYPE_INTELEPEER,
  SMS_CHANNEL_TYPE_BYOPSMS
];

export const webChatServiceDeskIntegrations = [
  NICE_INCONTACT_BYOSD_TYPE,
  GENESYS_SERVICEDESK_TYPE,
  SALESFORCE_SERVICEDESK_TYPE,
  ZENDESK_SERVICEDESK_TYPE
];

export const ootbWebChatServiceDeskIntegrations = [
  SALESFORCE_SERVICEDESK_TYPE,
  ZENDESK_SERVICEDESK_TYPE
];

export const channelCardFeatureFlags: Record<string, FeatureFlag | null> = {
  [FACEBOOK_CHANNEL_TYPE]: null, // No feature flag for Facebook
  [GENESYS_BOT_CONNECTOR_CHANNEL_TYPE]: null, // No feature flag for Genesys
  [SLACK_CHANNEL_TYPE]: null, // No feature flag for Slack
  [SMS_CHANNEL_TYPE]: FeatureFlag.TWILIO_TEXT_INTEGRATION,
  [TEAMS_CHANNEL_TYPE]: null, // No feature flag for Microsoft Teams
  [WHATSAPP_CHANNEL_TYPE]: null
};

export const smsFeatureFlags: Record<string, FeatureFlag | null> = {
  [INTELEPEER_SMS_TYPE]: null, // No feature flag for IntelepeerSMS
  [TWILIO_SMS_TYPE]: null, // No feature flag for TwilioSMS
  [GENESYS_SMS_TYPE]: FeatureFlag.CHANNELS_SMS_PROVIDER_GENESYS,
  [CUSTOM_SMS_TYPE]: FeatureFlag.CHANNELS_SMS_PROVIDER_CUSTOM
};

// Used to personalize the "Deploy your assistant" section of dashboard Task List
// This maps the integration preference selected in the Onboarding Wizard to its
// corresponding integration channel type.
export const wizardSelectionToType = {
  Web: WEBCHAT_CHANNEL_TYPE,
  Voice: PHONE_CHANNEL_TYPE,
  SMS: SMS_CHANNEL_TYPE,
  Slack: SLACK_CHANNEL_TYPE,
  Facebook: FACEBOOK_CHANNEL_TYPE,
  GenesysBotConnector: GENESYS_BOT_CONNECTOR_CHANNEL_TYPE,
  WhatsApp: WHATSAPP_CHANNEL_TYPE,
  MicrosoftTeams: TEAMS_CHANNEL_TYPE
};

// Old IA usage only!

export const supportedClassicIntegrations = [
  FACEBOOK_CHANNEL_TYPE,
  INTERCOM_CHANNEL_TYPE,
  PHONE_CHANNEL_TYPE,
  PREVIEW_CHANNEL_TYPE,
  SLACK_CHANNEL_TYPE,
  SMS_CHANNEL_TYPE,
  INTELEPEER_SMS_TYPE,
  WEBCHAT_CHANNEL_TYPE,
  WHATSAPP_CHANNEL_TYPE
];

export const thirdPartyChannels = [
  FACEBOOK_CHANNEL_TYPE,
  INTERCOM_CHANNEL_TYPE,
  SLACK_CHANNEL_TYPE,
  SMS_CHANNEL_TYPE,
  WHATSAPP_CHANNEL_TYPE
];

// No beta integrations as of today.
export const isBeta = (_channelType, _features) => false;
